/** Isolated DEV examples. Neither submissions nor decisions are authoritative. */
export type GovernanceKind = 'PRODUCT_MODEL' | 'LOCAL_ASSET';
export type GovernanceStatus = '待审核' | '审核中' | '退回' | '已通过';
export interface GovernanceRecord {
  readonly id: string;
  readonly name: string;
  readonly model: string;
  readonly kind: GovernanceKind;
  readonly submitter: string;
  readonly submitted: string;
  readonly version: string;
  readonly status: GovernanceStatus;
  readonly image: string | null;
  readonly category: string;
  readonly purpose: string;
  readonly capabilities: string;
  readonly scene: string;
}
export const governanceRecords: readonly GovernanceRecord[] = [
  {
    id: 'xt-100',
    name: '消防灭火机器人 XT-100',
    model: 'XT-100',
    kind: 'PRODUCT_MODEL',
    submitter: 'XX科技有限公司',
    submitted: '2024-12-10 14:30',
    version: 'v2',
    status: '待审核',
    image: '/portals/robot-rescue.png',
    category: '灭火设备',
    purpose: '建筑火灾扑救、危险环境作业',
    capabilities: '厂商声明：远程控制、环境感知',
    scene: '厂商用途声明：建筑火灾、化工灾害、隧道火灾',
  },
  {
    id: 'hm-200',
    name: '高压细水雾系统 HM-200',
    model: 'HM-200',
    kind: 'PRODUCT_MODEL',
    submitter: 'YY装备制造',
    submitted: '2024-12-09 16:20',
    version: 'v2',
    status: '退回',
    image: null,
    category: '灭火系统',
    purpose: '厂商声明：细水雾灭火',
    capabilities: '待补充技术材料，能力未核验',
    scene: '厂商用途声明：室内消防',
  },
  {
    id: 'uav-800',
    name: '消防无人机 UAV-800',
    model: 'UAV-800',
    kind: 'PRODUCT_MODEL',
    submitter: 'ZZ科技',
    submitted: '2024-12-08 11:15',
    version: 'v2',
    status: '审核中',
    image: '/portals/drone.png',
    category: '侦察无人机',
    purpose: '厂商声明：空中侦察',
    capabilities: '厂商声明：图像采集、环境观察',
    scene: '厂商用途声明：应急侦察',
  },
  {
    id: 'tank-3t',
    name: '市政消防水罐（3t）',
    model: 'LOCAL-TANK-03',
    kind: 'LOCAL_ASSET',
    submitter: '北京市消防支队（示例）',
    submitted: '2024-12-08 09:40',
    version: 'v2',
    status: '待审核',
    image: null,
    category: '本地消防装备资料',
    purpose: '机构描述：消防供水',
    capabilities: '本地配置未核验；可用状态未知',
    scene: '机构描述：辖区消防供水',
  },
  {
    id: 'hh-01',
    name: '智能消防头盔 HH-01',
    model: 'HH-01',
    kind: 'LOCAL_ASSET',
    submitter: 'AA机构（示例）',
    submitted: '2024-12-07 09:40',
    version: 'v2',
    status: '待审核',
    image: null,
    category: '个人防护装备资料',
    purpose: '机构描述：个人防护',
    capabilities: '资料声明，防护性能未核验',
    scene: '机构描述：消防作业',
  },
  {
    id: 'fr-300',
    name: '消防侦察机器人 FR-300',
    model: 'FR-300',
    kind: 'PRODUCT_MODEL',
    submitter: 'XX科技有限公司',
    submitted: '2024-12-06 15:30',
    version: 'v2',
    status: '已通过',
    image: '/portals/robot-recon.png',
    category: '侦察设备',
    purpose: '厂商声明：环境侦察',
    capabilities: '厂商声明：环境监测、远程图传',
    scene: '厂商用途声明：危险环境监测',
  },
];
export const governanceDemoTotals = {
  pending: 8,
  returned: 6,
  today: 4,
  published: 12,
  product: 32,
  asset: 24,
} as const;
export const governanceTrend = {
  submitted: [
    4, 9, 12, 10, 8, 10, 10, 10, 11, 15, 12, 9, 12, 11, 12, 15, 12, 7, 11, 10, 12, 14, 16,
  ],
  reviewed: [2, 3, 4, 6, 9, 7, 8, 7, 6, 5, 5, 6, 6, 8, 9, 8, 6, 4, 5, 6, 7, 9, 11],
  returned: [1, 1, 2, 3, 4, 5, 4, 5, 6, 5, 3, 4, 5, 4, 3, 6, 5, 4, 2, 3, 4, 5, 4],
} as const;
export function filterGovernanceRecords(
  query: string,
  kind = 'ALL',
  status = 'ALL',
  oldest = false,
): readonly GovernanceRecord[] {
  const result = governanceRecords.filter(
    (record) =>
      (kind === 'ALL' || record.kind === kind) &&
      (status === 'ALL' || record.status === status) &&
      `${record.name} ${record.model} ${record.submitter}`
        .toLowerCase()
        .includes(query.trim().toLowerCase()),
  );
  return oldest ? [...result].reverse() : result;
}
export const governanceChecks = [
  ['产品资料完整性', '已提供目录，内容仍需核对', '已列示'],
  ['技术参数真实性', '真实性未知，等待独立证明材料', '待确认'],
  ['内容合规性', '尚未进行正式合规审查', '待确认'],
  ['图片与资料质量', '示意素材，非设备实证照片', '待确认'],
  ['知识产权声明', '声明示例，权利尚未核验', '待确认'],
  ['应用场景描述', '提交方用途声明，非应用验证', '已列示'],
  ['安全风险提示', '需由责任 Owner 核对', '待确认'],
  ['其他补充材料', '检验与认证材料尚未核验', '待确认'],
] as const;
